import { CustomElement } from "../../../core/CustomElement";

// Utilities.
import { validateJsonStringified } from "../../../core/utilities";

// Components to use.
import "../label/label";
import { CCCLabel } from "../label/label";

export enum LabelEdge {
  top = "top",
  start = "start",
}

export enum TextAlign {
  start = "start",
  center = "center",
  end = "end",
}

export type Message = {
  text: string;
  severity: Severity;
};

export enum Severity {
  success = "success",
  warning = "warning",
  error = "error",
}

export enum FontWeight {
  normal = "normal",
  bold = "bold",
}

export class CCCField extends CustomElement {
  static get observedAttributes(): Array<string> {
    return [
      "label",
      "label-edge",
      "label-width",
      "label-align",
      "disabled",
      "internal-loading",
      "message",
      "label-weight",
    ];
  }

  constructor() {
    super();
    this.templateConfig = {
      template: require("./field.html").default,
      style: require("./field.scss").default,
    };
    this.setupComponent();
    // Adjust width for component.
    const $component = <HTMLElement>this.firstElementChild;
    if ($component) {
      $component.style.width = "100%";
    }
    this.label = null;
  }

  attributeChangedCallback(
    attr: string,
    _oldValue: string | null,
    newValue: string | null
  ): void {
    if (attr === "label") {
      this.label = newValue;
    }
    if (attr === "label-edge") {
      if (newValue in LabelEdge) {
        this.labelEdge = <LabelEdge>newValue;
      }
    }
    if (attr === "label-width") {
      this.labelWidth = newValue;
    }
    if (attr === "label-align") {
      if (newValue in TextAlign) {
        this.labelAlign = <TextAlign>newValue;
      }
    }
    if (attr === "label-weight" && newValue in FontWeight) {
      this.labelWeight = <FontWeight>newValue;
    }
    if (attr === "disabled") {
      if (newValue === null) {
        this.disabled = false;
      } else {
        this.disabled = true;
      }
    }
    if (attr === "internal-loading") {
      if (newValue === null) {
        this.internalLoading = false;
      } else {
        this.internalLoading = true;
      }
    }
    if (attr === "message") {
      if (newValue === null) {
        this.message = null;
      } else {
        const newValueObj: Message = validateJsonStringified(newValue) ?? {};
        if (
          newValueObj &&
          newValueObj.text &&
          newValueObj.severity &&
          newValueObj.severity in Severity
        ) {
          this.message = newValueObj;
        }
      }
    }
  }

  public set label(value: string) {
    const $label: CCCLabel = this.shadowDom.querySelector(".label");
    if (!value || value.length === 0) {
      $label.style.display = "none";
    } else {
      $label.setAttribute("label", value);
      $label.style.display = "block";
    }
  }

  public set labelEdge(value: LabelEdge) {
    const $labelContainer: CustomElement =
      this.shadowDom.querySelector(".container");
    switch (value) {
      case LabelEdge.top: {
        $labelContainer.classList.add("label-edge-top");
        $labelContainer.classList.remove("label-edge-start");
        break;
      }
      case LabelEdge.start: {
        $labelContainer.classList.add("label-edge-start");
        $labelContainer.classList.remove("label-edge-top");
        break;
      }
    }
  }

  public set labelWidth(value: string) {
    const $label: CCCLabel = this.shadowDom.querySelector(".label");
    // Validate if value is in 'px' or '%'.
    const regex = /^\d+((p{1}x{1})|%)/;
    if (regex.test(value)) {
      $label.style.minWidth = value;
    }
  }

  public set labelAlign(value: TextAlign) {
    const $label: CCCLabel = this.shadowDom.querySelector(".label");
    switch (value) {
      case TextAlign.start: {
        $label.setAttribute("text-align", TextAlign.start);
        break;
      }
      case TextAlign.center: {
        $label.setAttribute("text-align", TextAlign.center);
        break;
      }
      case TextAlign.end: {
        $label.setAttribute("text-align", TextAlign.end);
        break;
      }
    }
  }

  public set labelWeight(value: FontWeight) {
    const $label: CCCLabel = this.shadowDom.querySelector(".label");
    $label.setAttribute("label-weight", value.toString());
  }

  public set disabled(value: boolean) {
    const $component: CustomElement =
      this.shadowDom.querySelector(".component");
    if ($component) {
      if (value) {
        $component.classList.add("disabled");
      } else {
        $component.classList.remove("disabled");
      }
    }
  }

  public set internalLoading(value: boolean) {
    const $loader: CustomElement = this.shadowDom.querySelector("#loader");
    if ($loader) {
      if (value) {
        $loader.classList.remove("hidden");
      } else {
        $loader.classList.add("hidden");
      }
    }
  }

  public set message(value: Message | null) {
    const $cccField: HTMLElement = this.shadowDom.querySelector(".ccc-field");
    const $component = this.shadowDom.querySelector(".component");
    const $message = this.shadowDom.querySelector(".message");
    $cccField.style.marginBottom = "unset";
    for (const severity in Severity) {
      $component.classList.remove(severity);
      $message.classList.remove(severity);
    }
    $message.innerHTML = "";
    $message.classList.add("hidden");
    if (value) {
      $cccField.style.marginBottom = "1.2rem";
      $component.classList.add(value.severity);
      $message.innerHTML = value.text;
      $message.classList.add(value.severity);
      $message.classList.remove("hidden");
    }
  }
}

// Define component
if (!customElements.get("ccc-field")) {
  customElements.define("ccc-field", CCCField);
}
